(function () {
  "use strict";

  const ROOT_SELECTOR = "[data-store-locator-root]";
  const OSM_ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
  const MAPBOX_ATTRIBUTION =
    OSM_ATTRIBUTION + ' &copy; <a href="https://carto.com/attributions">CARTO</a>';
  // CARTO watermarks unkeyed tiles. A valid key keeps CARTO. Otherwise the
  // map uses OSM raster tiles for the current viewport only (no prefetch).
  const BASEMAP_TILE_URL = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
  const OSM_FALLBACK_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

  // Mirrored verbatim from lib/public-store-identity.js. A theme asset cannot
  // import from lib/; lib/public-store-identity.test.js fails on any drift.
  /* PUBLIC-STORE-IDENTITY:START */
// Dokkaebier's own closed venue. Never public copy, but not an account
// annotation either, so it is kept out of the internal-label checks that drive
// store ids and the upstream-repair counter.
const CLOSED_LOCATION_TERM = ["tap", "room"].join("");

// Delimiters an operator uses to annotate a record. A retailer's real name
// never contains them, so the whole annotation goes.
const INTERNAL_ANNOTATION_WRAPPERS = [/\*[^*]*\*/g, /\[[^\]]*\]/g, /\{[^}]*\}/g, /<[^>]*>/g];

// Account-status phrases observed in or plausible for the operations feed.
// Multi-word, so they are specific enough to strip wherever they appear.
const INTERNAL_LABEL_PHRASES = [
  "credit hold",
  "hold credit",
  "account hold",
  "hold account",
  "payment hold",
  "hold payment",
  "shipping hold",
  "hold shipping",
  "on hold",
  "past due",
  "bad debt",
  "in collections",
  "do not ship",
  "do not sell",
  "do not order",
  "do not contact",
  "test account",
  "internal only",
  "internal use",
  "duplicate record",
];

// Single words that are also ordinary English. "Delinquent Brewing" and
// "Unpaid Bills Bar" are plausible retailer names, so these only count as an
// annotation when a delimiter or the end of the field sets them apart.
const DELIMITED_LABEL_PHRASES = ["delinquent", "unpaid", "chargeback", "collections"];

const STRAY_ANNOTATION_CHARACTERS = /[*[\]{}<>]/g;
// A parenthetical can be part of a real name, so it survives unless removing the
// label left it empty.
const EMPTIED_PARENTHETICAL = /\(\s*\)/g;
const EDGE_SEPARATORS = /^[\s\-–—:,|/]+|[\s\-–—:,|/]+$/g;
// What an operator puts between a name and an annotation. Whitespace alone is
// deliberately excluded: with a space counting as a delimiter, "Delinquent
// Brewing" would be read as an annotation and published as "Brewing".
const STRONG_DELIMITER = "[\\-–—:,|/()\\[\\]{}<>*]";

// Latin lookalikes. Folding these is what stops a Cyrillic "е" smuggling a
// label past the phrase list. Detection only — never applied to what we print.
const CONFUSABLES = {
  "\u0430": "a", "\u0435": "e", "\u043e": "o", "\u0440": "p", "\u0441": "c",
  "\u0443": "y", "\u0445": "x", "\u0456": "i", "\u0458": "j", "\u0455": "s",
  "\u04bb": "h", "\u0501": "d", "\u03b1": "a", "\u03bf": "o", "\u03c1": "p",
  "\u03b9": "i", "\u03bd": "v", "\u0261": "g",
};
const CONFUSABLE_PATTERN = new RegExp("[" + Object.keys(CONFUSABLES).join("") + "]", "g");
// Zero-width and soft-hyphen characters, which split a word invisibly.
const INVISIBLE_CHARACTERS = /[\u00ad\u200b-\u200f\u2060\ufeff]/g;

function escapeForPattern(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Detection copy: invisible characters gone, lookalikes folded, accents split
// off, lowercased. Never displayed.
function foldForDetection(value) {
  return String(value == null ? "" : value)
    .replace(INVISIBLE_CHARACTERS, "")
    .toLowerCase()
    .replace(CONFUSABLE_PATTERN, (character) => CONFUSABLES[character])
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Words may be run together or joined by punctuation, and a trailing plural is
// still the same annotation: "CreditHold", "Credit.Hold", "Credit Holds".
function internalLabelPattern(phrase) {
  const words = phrase.split(" ").map(escapeForPattern);
  return new RegExp("(^|[^a-z0-9])" + words.join("[^a-z0-9]{0,3}") + "s?($|[^a-z0-9])", "gi");
}

// Set apart by a delimiter or the edge of the field on both sides, so an
// ordinary word inside a real name is left alone.
function delimitedLabelPattern(phrase) {
  const word = escapeForPattern(phrase);
  return new RegExp(
    "(^|" + STRONG_DELIMITER + ")\\s*" + word + "s?\\s*($|" + STRONG_DELIMITER + ")",
    "gi"
  );
}

function closedLocationPattern() {
  return new RegExp("(^|[^a-z0-9])" + CLOSED_LOCATION_TERM + "s?($|[^a-z0-9])", "gi");
}

function labelPatterns() {
  return INTERNAL_LABEL_PHRASES.map(internalLabelPattern)
    .concat(DELIMITED_LABEL_PHRASES.map(delimitedLabelPattern));
}

// Does this text still carry an internal ACCOUNT annotation? The closed venue
// is not one, so it is excluded here on purpose.
function hasInternalLabel(value) {
  const text = foldForDetection(value);
  if (!text) return false;
  if (INTERNAL_ANNOTATION_WRAPPERS.some((pattern) => new RegExp(pattern.source, "i").test(text))) return true;
  return labelPatterns().some((pattern) => pattern.test(text));
}

function hasClosedLocationTerm(value) {
  return closedLocationPattern().test(foldForDetection(value));
}

// Anything at all that must not reach a customer.
function hasPublicTextProblem(value) {
  return hasInternalLabel(value) || hasClosedLocationTerm(value);
}

function stripOnce(text) {
  let out = text;
  INTERNAL_ANNOTATION_WRAPPERS.forEach((pattern) => {
    out = out.replace(pattern, " ");
  });
  labelPatterns().concat([closedLocationPattern()]).forEach((pattern) => {
    out = out.replace(pattern, "$1 $2");
  });
  return out
    .replace(STRAY_ANNOTATION_CHARACTERS, " ")
    .replace(/\s{2,}/g, " ")
    .replace(EMPTIED_PARENTHETICAL, " ")
    .replace(/\s{2,}/g, " ")
    .replace(EDGE_SEPARATORS, "")
    .trim();
}

// One pass is not enough: a replace is non-overlapping, so "Credit Credit Hold
// Hold" would come out as a clean "Credit Hold". Repeat until nothing changes.
function stripInternalLabels(value) {
  let text = String(value == null ? "" : value);
  for (let pass = 0; pass < 5; pass += 1) {
    const next = stripOnce(text);
    if (next === text) return next;
    text = next;
  }
  return text;
}

// Fail closed. If stripping could not clean it, publish nothing from it.
function publicStoreText(rawValue) {
  const stripped = stripInternalLabels(rawValue);
  return hasPublicTextProblem(stripped) ? "" : stripped;
}

function publicStoreName(rawName) {
  const stripped = stripInternalLabels(rawName);
  const corrected = /^Una M\?s$/i.test(stripped) ? "Una Más" : stripped;
  if (!corrected || hasPublicTextProblem(corrected)) return "Retailer";
  return corrected;
}

function publicStoreWebsite(rawWebsite) {
  const text = String(rawWebsite == null ? "" : rawWebsite).trim();
  if (!text) return "";
  // A URL has no word boundaries, so the closed-venue term is matched anywhere.
  if (new RegExp(CLOSED_LOCATION_TERM, "i").test(foldForDetection(text))) return "";
  // Only the phrase list here. The wrapper rules would blank an ordinary URL
  // for a bracketed query parameter, which is a link a customer needs.
  const folded = foldForDetection(text);
  return labelPatterns().some((pattern) => pattern.test(folded)) ? "" : text;
}

// Cheap deterministic digest. Not security; it exists so two rows whose feed
// ids differ only inside the annotation keep two different public ids.
function shortHash(value) {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash * 33) ^ value.charCodeAt(index)) >>> 0;
  }
  return hash.toString(36);
}

function slugify(value) {
  return String(value == null ? "" : value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Identity stays anchored to the feed's own key wherever possible. Deriving it
// from the sanitized name instead would collapse two annotated rows at one
// address onto a single id, and the map de-duplicates by id, so one of the two
// real retailers would silently vanish.
function publicStoreId(row) {
  const feedId = String(row.id == null ? "" : row.id).trim();
  if (feedId && !hasInternalLabel(feedId)) return "sheet-" + feedId;

  // Stripping alone is not enough to stay unique: two rows for one address whose
  // feed ids differ only by "credit-hold-" and "past-due-" clean down to the
  // same string, and the map de-duplicates by id, so one real retailer would
  // disappear from the results. The digest of the original id keeps them apart
  // and keeps each one stable across syncs.
  const cleanedFeedId = slugify(stripInternalLabels(feedId));
  if (cleanedFeedId) return "sheet-" + cleanedFeedId + "-" + shortHash(feedId);

  const coordinates = [row.latitude, row.longitude].map((value) =>
    Number.isFinite(Number(value)) && String(value).trim() !== "" ? Number(value).toFixed(5) : ""
  );
  const slug = slugify(
    [publicStoreName(row.storeName), row.address, row.city, row.state, row.zip]
      .concat(coordinates)
      .map((value) => String(value == null ? "" : value))
      .join("|")
  );

  return slug ? "store-" + slug : "store-unlisted";
}
/* PUBLIC-STORE-IDENTITY:END */
  const TYPE_META = {
    Grocery: { label: "Grocery", icon: "GR", tone: "#22c55e", className: "store-locator-pill--grocery", order: 10 },
    "Bottle Shop": { label: "Bottle Shop", icon: "BS", tone: "#a855f7", className: "store-locator-pill--bottle", order: 20 },
    "Bar & Restaurant": {
      label: "Bar & Restaurant",
      icon: "BR",
      tone: "#ef4444",
      className: "store-locator-pill--bar",
      order: 30,
    },
    Hotel: { label: "Hotel", icon: "HT", tone: "#f59e0b", className: "", order: 40 },
  };
  const FORMAT_META = {
    all: { label: "All formats", icon: "", order: 0 },
    Can: { label: "Can", icon: "CA", order: 10 },
    Draft: { label: "Draft", icon: "DR", order: 20 },
  };

  class StoreLocator {
    constructor(root, config) {
      this.root = root;
      this.config = config || {};
      this.productMeta = this.config.productMeta || {};
      this.state = {
        stores: [],
        filteredStores: [],
        selectedId: null,
        queryText: "",
        filters: { region: "all", type: "all", flavor: this.config.initialFlavor || "all", fmt: "all" },
        loadState: { stage: "idle", total: (this.config.sheetUrls || []).length, failed: 0 },
        locationAnchor: null,
        maxDistance: null,
        notice: null,
      };
      this.map = null;
      this.markerLayer = null;
      this.markers = new Map();
      this.userMarker = null;
      this.activePopup = null;
      this.isLoading = false;
      this.locationRequestToken = 0;
      this.resizeTimer = null;
      this.dom = {
        queryInput: root.querySelector('[data-locator-input="query"]'),
        locationInput: root.querySelector('[data-locator-input="location"]') || null,
        status: root.querySelector("[data-locator-status]"),
        summary: root.querySelector("[data-locator-summary]"),
        filterContainers: {
          region: root.querySelector('[data-locator-filter="region"]'),
          type: root.querySelector('[data-locator-filter="type"]'),
          flavor: root.querySelector('[data-locator-filter="flavor"]'),
          fmt: root.querySelector('[data-locator-filter="fmt"]'),
        },
        searchClear: root.querySelector('[data-locator-action="clear-search"]'),
        selected: Array.from(root.querySelectorAll("[data-locator-selected]")),
        results: Array.from(root.querySelectorAll("[data-locator-results]")),
        visibleCounts: Array.from(root.querySelectorAll("[data-locator-visible-count]")),
        ordering: Array.from(root.querySelectorAll("[data-locator-ordering]")),
        mapCaption: root.querySelector("[data-locator-map-caption]"),
        retryButtons: Array.from(root.querySelectorAll('[data-locator-action="retry"]')),
        fitButtons: Array.from(root.querySelectorAll('[data-locator-action="fit-results"]')),
        statStores: root.querySelector('[data-locator-stat="stores"]'),
        statRegions: root.querySelector('[data-locator-stat="regions"]'),
        statBrews: root.querySelector('[data-locator-stat="brews"]'),
        map: root.querySelector("[data-locator-map]"),
      };
      this.handleRootClick = this.handleRootClick.bind(this);
      this.handleQueryInput = this.handleQueryInput.bind(this);
      this.handleQueryKeydown = this.handleQueryKeydown.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    init() {
      this.renderFilters();
      this.bindEvents();
      this.readUrlState();
      this.updateStaticStats();
      this.renderAll();

      if (typeof window.L === "undefined") {
        this.state.loadState = {
          stage: "error",
          total: (this.config.sheetUrls || []).length,
          failed: (this.config.sheetUrls || []).length,
        };
        this.state.notice = { tone: "error", message: "The map library did not load, so the locator is unavailable." };
        this.renderAll();
        return;
      }

      this.initMap();
      this.loadStores();
      // Mobile: start with sheet collapsed so map dominates
      if (window.innerWidth <= 960) {
        this.collapseMobileSheet();
        this.showLocationPrompt();
      }
    }

    bindEvents() {
      this.root.addEventListener("click", this.handleRootClick);
      this.dom.queryInput.addEventListener("input", this.handleQueryInput);
      this.dom.queryInput.addEventListener("keydown", this.handleQueryKeydown.bind(this));
      // iOS search event — fires when user taps "Search" on mobile keyboard
      this.dom.queryInput.addEventListener("search", () => this.handleSearchSubmit());
      // Form submit — catches Enter key and iOS keyboard Search button
      const searchForm = this.root.querySelector("[data-locator-search-form]");
      if (searchForm) searchForm.addEventListener("submit", (e) => { e.preventDefault(); this.handleSearchSubmit(); });
      window.addEventListener("resize", this.handleResize);
      // Mobile bottom sheet handle
      const handle = this.root.querySelector(".store-locator-sheet__handle");
      if (handle) handle.addEventListener("click", () => this.toggleMobileSheet());

      // Mobile bottom sheet swipe gesture
      const sheet = this.root.querySelector(".store-locator-sheet");
      if (sheet) {
        let touchStartY = 0;
        let touchDelta = 0;
        sheet.addEventListener("touchstart", (e) => {
          touchStartY = e.touches[0].clientY;
          touchDelta = 0;
        }, { passive: true });
        sheet.addEventListener("touchmove", (e) => {
          touchDelta = e.touches[0].clientY - touchStartY;
        }, { passive: true });
        sheet.addEventListener("touchend", () => {
          if (Math.abs(touchDelta) < 40) return; // ignore small drags
          if (touchDelta < 0) {
            this.expandMobileSheet();   // swipe up → expand
          } else {
            this.collapseMobileSheet(); // swipe down → collapse
          }
        });
      }
    }

    handleRootClick(event) {
      const tracked = event.target.closest("[data-locator-track]");
      if (tracked && this.root.contains(tracked)) {
        this.trackEvent(tracked.dataset.locatorTrack, tracked.dataset.storeName || "");
      }

      const target = event.target.closest("button, [data-store-id]");
      if (!target || !this.root.contains(target)) return;

      if (target.dataset.filterKey) {
        this.setFilter(target.dataset.filterKey, target.dataset.filterValue);
        return;
      }

      if (target.dataset.storeId) {
        this.selectStore(target.dataset.storeId, { pan: true, openPopup: true, scrollIntoView: false });
        return;
      }

      if (!target.dataset.locatorAction) return;

      switch (target.dataset.locatorAction) {
        case "apply-location":
          this.applyLocationQuery();
          break;
        case "locate":
          this.useMyLocation();
          break;
        case "clear-all":
          this.clearAll();
          break;
        case "fit-results":
          this.fitResults();
          break;
        case "retry":
          this.loadStores();
          break;
        case "clear-selection":
          this.clearSelection();
          break;
        case "set-distance":
          this.setMaxDistance(target.dataset.distance);
          break;
        case "toggle-filters":
          this.toggleFilters();
          break;
        case "dismiss-prompt":
          this.hideLocationPrompt();
          break;
        case "clear-search":
          this.clearSearch();
          break;
        case "search-submit":
          this.handleSearchSubmit();
          break;
        default:
          break;
      }
    }

    handleQueryInput() {
      this.state.queryText = this.dom.queryInput.value || "";
      this.state.notice = null;
      this.applyDerivedState({ openPopup: false });
    }

    handleQueryKeydown(event) {
      if (event.key !== "Enter") return;
      event.preventDefault();
      const value = this.normalizeCell(this.dom.queryInput.value);
      if (!value) return;
      // Always try to geocode — set location anchor for distance sorting
      this.applyLocationFromQuery(value);
    }

    applyLocationFromQuery(value) {
      const token = this.locationRequestToken + 1;
      this.locationRequestToken = token;
      this.state.notice = { tone: "loading", message: "Searching for " + value + "..." };
      this.renderAll();

      fetch(
        "https://nominatim.openstreetmap.org/search?format=jsonv2&countrycodes=us&limit=1&q=" + encodeURIComponent(value)
      )
        .then(function (response) {
          if (!response.ok) throw new Error("HTTP " + response.status);
          return response.json();
        })
        .then((payload) => {
          if (token !== this.locationRequestToken) return;
          if (!payload || !payload.length) {
            this.state.notice = { tone: "warning", message: "No location match for " + value + ". Showing text matches." };
            this.renderAll();
            return;
          }
          const match = payload[0];
          this.setLocationAnchor({
            lat: Number.parseFloat(match.lat),
            lng: Number.parseFloat(match.lon),
            label: this.compactPlaceLabel(match.display_name || value),
            source: "search",
          });
          // Clear text filter so stores aren't filtered by the zip text
          this.state.queryText = "";
          this.state.notice = null;
          // Show the matched location in the search input
          this.dom.queryInput.value = this.compactPlaceLabel(match.display_name || value);
          // Auto-set distance to 10mi so results reflect nearby stores
          this.state.maxDistance = 10;
          this.updateDistanceChips();
          this.hideLocationPrompt();
          this.applyDerivedState({ refit: "results", openPopup: false });
          // Keep sheet collapsed — map first, always
          this.collapseMobileSheet();
        })
        .catch(() => {
          if (token !== this.locationRequestToken) return;
          this.state.notice = { tone: "warning", message: "Location lookup failed." };
          this.renderAll();
        });
    }

    setMaxDistance(value) {
      const miles = value === "all" ? null : Number.parseInt(value, 10);
      this.state.maxDistance = miles;
      this.updateDistanceChips();
      this.applyDerivedState({ refit: "results", openPopup: false });
    }

    updateDistanceChips() {
      const distVal = this.state.maxDistance;
      this.root.querySelectorAll("[data-locator-action='set-distance']").forEach((btn) => {
        const d = btn.dataset.distance;
        const active = distVal === null ? d === "all" : String(distVal) === d;
        btn.setAttribute("aria-pressed", active ? "true" : "false");
      });
    }

    toggleFilters() {
      const filtersEl = this.root.querySelector(".store-locator-filters");
      if (filtersEl) filtersEl.classList.toggle("is-open");
      // Also toggle distance row on mobile
      const distRow = this.root.querySelector(".store-locator-toolbar__distance-row");
      if (distRow) distRow.classList.toggle("is-open");
    }

    toggleMobileSheet() {
      const sheet = this.root.querySelector(".store-locator-sheet");
      if (sheet) sheet.classList.toggle("is-collapsed");
      this.renderMobileSheetTitle();
    }

    collapseMobileSheet() {
      const sheet = this.root.querySelector(".store-locator-sheet");
      if (sheet) sheet.classList.add("is-collapsed");
      this.renderMobileSheetTitle();
    }

    toggleFilterPanel(panelId) {
      const panel = this.root.querySelector("[data-panel-id=\"" + panelId + "\"]");
      const tab = this.root.querySelector("[data-filter-panel=\"" + panelId + "\"]");
      if (!panel) return;
      const wasOpen = panel.classList.contains("is-open");
      // Close all panels and deactivate all tabs
      this.root.querySelectorAll(".store-locator-filter-panel").forEach(function(p) { p.classList.remove("is-open"); p.style.display = "none"; });
      this.root.querySelectorAll(".store-locator-filter-tab").forEach(function(t) { t.classList.remove("is-active"); });
      // If it wasn't open, open it
      if (!wasOpen) {
        panel.classList.add("is-open");
        panel.style.display = "";
        if (tab) tab.classList.add("is-active");
      }
    }

    expandMobileSheet() {
      const sheet = this.root.querySelector(".store-locator-sheet");
      if (sheet) sheet.classList.remove("is-collapsed");
      this.renderMobileSheetTitle();
    }

    showLocationPrompt() {
      const prompt = this.root.querySelector("[data-locator-location-prompt]");
      if (prompt && navigator.geolocation) prompt.style.display = "";
    }

    hideLocationPrompt() {
      const prompt = this.root.querySelector("[data-locator-location-prompt]");
      if (prompt) prompt.style.display = "none";
    }

    handleSearchSubmit() {
      const value = this.normalizeCell(this.dom.queryInput.value);
      if (!value) return;
      this.hideLocationPrompt();
      this.dom.queryInput.blur(); // dismiss mobile keyboard
      this.applyLocationFromQuery(value);
    }

    clearSearch() {
      this.dom.queryInput.value = "";
      this.state.queryText = "";
      this.state.locationAnchor = null;
      this.state.maxDistance = null;
      this.locationRequestToken += 1;
      if (this.userMarker && this.map) {
        this.map.removeLayer(this.userMarker);
      }
      this.userMarker = null;
      this.applyDerivedState({ refit: "default", openPopup: false });
    }

    handleResize() {
      if (!this.map) return;
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.map.invalidateSize();
      }, 120);
    }

    readUrlState() {
      const params = new URLSearchParams(window.location.search);
      const query = params.get("q");
      const region = params.get("region");
      const type = params.get("type");
      const flavor = params.get("flavor");
      const fmt = params.get("fmt");

      if (query) {
        this.state.queryText = query;
        this.dom.queryInput.value = query;
      }
      if (region) this.state.filters.region = region;
      if (type) this.state.filters.type = type;
      if (flavor) this.state.filters.flavor = flavor;
      if (fmt) this.state.filters.fmt = fmt;
    }

    syncUrlState() {
      const params = new URLSearchParams(window.location.search);
      const filterMap = {
        q: (this.state.queryText || "").trim(),
        region: this.state.filters.region,
        type: this.state.filters.type,
        flavor: this.state.filters.flavor,
        fmt: this.state.filters.fmt,
      };

      Object.keys(filterMap).forEach((key) => {
        const value = filterMap[key];
        const isDefault = value === "" || value === "all";
        if (isDefault) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });

      const next = params.toString();
      const nextUrl = next ? `${window.location.pathname}?${next}` : window.location.pathname;
      window.history.replaceState({}, "", nextUrl);
    }

    renderFilters() {
      const groups = this.buildFilterOptions();
      Object.keys(groups).forEach((key) => {
        const container = this.dom.filterContainers[key];
        if (!container) return;
        container.innerHTML = groups[key]
          .map((option) => {
            const selected = this.state.filters[key] === option.value;
            const count = this.getFilterOptionCount(key, option.value);
            const disabled = option.value !== "all" && count === 0;
            const chipStyle = option.tone ? ` style="--chip-tone:${this.escapeAttribute(option.tone)}"` : "";
            let icon = "";
            if (option.useFavicon) {
              icon = `<img class="store-locator-chip__favicon" src="https://enjoydkb.com/cdn/shop/files/DKB_FullLogo_red_black_RGB_5d2132b7-8295-4e82-9f06-fd7ac94ac741.png?crop=center&height=20&v=1733811610&width=20" alt="" width="16" height="16">`;
            } else if (option.emoji) {
              icon = `<span class="store-locator-chip__emoji">${option.emoji}</span>`;
            } else if (option.icon) {
              icon = `<span class="store-locator-chip__icon"${chipStyle}>${this.escapeHtml(option.icon)}</span>`;
            }
            const countMarkup = "";

            return (
              `<button type="button" class="store-locator-chip${disabled ? " is-disabled" : ""}"` +
              ` data-filter-key="${this.escapeAttribute(key)}"` +
              ` data-filter-value="${this.escapeAttribute(option.value)}"` +
              ` aria-pressed="${selected ? "true" : "false"}"` +
              `${disabled ? ' disabled="disabled"' : ""}>` +
              icon +
              `<span>${this.escapeHtml(option.label)}</span>` +
              countMarkup +
              `</button>`
            );
          })
          .join("");
      });
    }

    buildFilterOptions() {
      const regionEmoji = {};
      const regions = Object.keys(this.config.regionBounds || {});
      const regionOptions = [{ value: "all", label: "All areas", emoji: "\ud83c\udf0d", tone: "" }].concat(
        regions.map((name) => ({
          value: name,
          label: name,
          emoji: regionEmoji[name] || "",
          tone: "",
        }))
      );

      const typeEmoji = { "Grocery": "\ud83d\uded2", "Bottle Shop": "\ud83c\udf7e", "Bar & Restaurant": "\ud83c\udf7d\ufe0f", "Hotel": "\ud83c\udfe8" };
      const typeNames = Object.keys(TYPE_META).sort((left, right) => TYPE_META[left].order - TYPE_META[right].order);
      const typeOptions = [{ value: "all", label: "All channels", emoji: "", tone: "" }].concat(
        typeNames.map((name) => ({
          value: name,
          label: TYPE_META[name].label,
          emoji: typeEmoji[name] || "",
          tone: TYPE_META[name].tone,
        }))
      );

      const flavorEmoji = { "Yuza Blonde": "\ud83c\udf4b", "K-Beer": "\u262f\ufe0f", "Kimchi Sour": "\ud83c\udf36\ufe0f", "Lychee Blonde": "\ud83c\udf38", "Dokkaebi IPA": "", "Milk Stout": "\ud83e\udd5b" };
      const flavorNames = this.collectFlavorNames();
      const flavorOptions = [{ value: "all", label: "All beers", emoji: "", tone: "" }].concat(
        flavorNames.map((name) => {
          const meta = this.getProductMeta(name);
          const emoji = flavorEmoji[name];
          return { value: name, label: meta.label, emoji: emoji !== undefined ? emoji : "", tone: meta.tone, useFavicon: name === "Dokkaebi IPA" };
        })
      );

      const formatOptions = [{ value: "all", label: "All types", emoji: "", tone: "" }].concat(
        ["Can", "Draft"].map((name) => ({
          value: name,
          label: FORMAT_META[name].label,
          emoji: name === "Can" ? "\ud83e\udd6b" : "\ud83c\udf7b",
          tone: name === "Can" ? "#b8954f" : "#6e93d6",
        }))
      );

      return { region: regionOptions, type: typeOptions, flavor: flavorOptions, fmt: formatOptions };
    }

    collectFlavorNames() {
      const names = Object.keys(this.productMeta);

      this.state.stores.forEach((store) => {
        store.products.forEach((product) => {
          if (names.indexOf(product.name) === -1) names.push(product.name);
        });
      });

      return names.sort((left, right) => {
        const leftMeta = this.getProductMeta(left);
        const rightMeta = this.getProductMeta(right);
        if (leftMeta.order !== rightMeta.order) return leftMeta.order - rightMeta.order;
        return left.localeCompare(right);
      });
    }

    matchesRegionFilter(store, filterValue) {
      if (!filterValue || filterValue === "all") return true;
      var stateAbbrs = { "California": ["CA", "California"], "Wisconsin": ["WI", "Wisconsin"], "Illinois": ["IL", "Illinois"] };
      var matchVals = stateAbbrs[filterValue] || [filterValue];
      var storeState = (store.state || "").trim();
      return matchVals.indexOf(storeState) !== -1 || store.region === filterValue;
    }

    getFilterOptionCount(key, value) {
      // Count stores that match all OTHER active filters, then check this filter's value.
      // This makes counts interactive — they update based on currently active filters.
      const filters = this.state.filters;
      const query = (this.state.queryText || "").trim().toLowerCase();
      const stores = this.state.stores.filter((store) => {
        // Apply all filters EXCEPT the one we're counting for
        if (key !== "region" && filters.region !== "all" && !this.matchesRegionFilter(store, filters.region)) return false;
        if (key !== "type" && filters.type !== "all" && store.type !== filters.type) return false;
        if (key !== "flavor" && filters.flavor !== "all" && !store.products.some((p) => p.name === filters.flavor)) return false;
        if (key !== "fmt" && filters.fmt !== "all") {
          const fmtMatch = store.products.some((p) => {
            if (filters.flavor !== "all" && p.name !== filters.flavor) return false;
            return p.formats.indexOf(filters.fmt) !== -1;
          });
          if (!fmtMatch) return false;
        }
        // Apply distance filter if active
        if (this.state.maxDistance && this.state.locationAnchor) {
          const dist = this.haversine(this.state.locationAnchor.lat, this.state.locationAnchor.lng, store.lat, store.lng);
          if (dist > this.state.maxDistance) return false;
        }
        // Apply text query
        if (query) {
          const searchBlob = [store.name, store.addr, store.city, store.state, store.zip, store.region, store.type, store.products.map((p) => p.name).join(" ")].join(" ").toLowerCase();
          if (searchBlob.indexOf(query) === -1) return false;
        }
        return true;
      });

      if (value === "all") return stores.length;
      if (key === "region") return stores.filter((store) => this.matchesRegionFilter(store, value)).length;
      if (key === "type") return stores.filter((store) => store.type === value).length;
      if (key === "flavor") {
        return stores.filter((store) => store.products.some((product) => product.name === value)).length;
      }
      if (key === "fmt") {
        return stores.filter((store) => store.products.some((product) => product.formats.indexOf(value) !== -1)).length;
      }
      return 0;
    }

    updateStaticStats() {
      const totalStores = this.state.stores.length;
      const regionNames = new Set();

      this.state.stores.forEach((store) => {
        if (store.region) regionNames.add(store.region);
      });

      if (this.dom.statStores) this.dom.statStores.textContent = totalStores;
      if (this.dom.statRegions) {
        this.dom.statRegions.textContent = regionNames.size || Object.keys(this.config.regionBounds || {}).length;
      }
      if (this.dom.statBrews) this.dom.statBrews.textContent = this.collectFlavorNames().length;
    }

    basemapTileUrl() {
      const key = typeof this.config.basemapApiKey === "string" ? this.config.basemapApiKey.trim() : "";
      // A key is an opaque token. Anything else is a paste accident.
      if (!key || !/^[A-Za-z0-9_-]+$/.test(key)) return OSM_FALLBACK_TILE_URL;
      return BASEMAP_TILE_URL + (BASEMAP_TILE_URL.indexOf("?") === -1 ? "?" : "&") + "key=" + encodeURIComponent(key);
    }

    basemapAttribution() {
      const key = typeof this.config.basemapApiKey === "string" ? this.config.basemapApiKey.trim() : "";
      if (!key || !/^[A-Za-z0-9_-]+$/.test(key)) return OSM_ATTRIBUTION;
      return MAPBOX_ATTRIBUTION;
    }

    initMap() {
      const center = Array.isArray(this.config.defaultCenter) ? this.config.defaultCenter : [39.5, -98.35];
      const zoom = Number.isFinite(this.config.defaultZoom) ? this.config.defaultZoom : 4;
      this.map = window.L.map(this.dom.map, { zoomControl: false, attributionControl: true }).setView(center, zoom);
      this.markerLayer = window.L.layerGroup().addTo(this.map);

      window.L.tileLayer(this.basemapTileUrl(), {
        maxZoom: 19,
        attribution: this.basemapAttribution(),
      }).addTo(this.map);
      const zoomPos = window.innerWidth <= 960 ? "bottomleft" : "topright";
      window.L.control.zoom({ position: zoomPos }).addTo(this.map);

      window.setTimeout(() => {
        this.map.invalidateSize();
      }, 180);
    }

    loadStores() {
      if (this.isLoading) return;
      this.isLoading = true;
      this.state.loadState = {
        stage: "loading",
        total: (this.config.sheetUrls || []).length,
        failed: 0,
      };
      this.state.notice = null;
      this.renderAll();

      Promise.allSettled((this.config.sheetUrls || []).map((url) => this.fetchSheet(url)))
        .then((results) => {
          let stores = [];
          let successfulFeeds = 0;
          let failedFeeds = 0;

          results.forEach((result) => {
            if (result.status === "fulfilled") {
              successfulFeeds += 1;
              stores = stores.concat(result.value);
            } else {
              failedFeeds += 1;
            }
          });

          this.state.stores = this.dedupeStores(stores);
          this.renderFilters();
          this.updateStaticStats();

          if (!successfulFeeds) {
            this.state.loadState = {
              stage: "error",
              total: results.length,
              failed: failedFeeds,
            };
            this.clearMapMarkers();
            this.clearSelection(false);
          } else {
            this.state.loadState = {
              stage: failedFeeds ? "partial" : "ready",
              total: results.length,
              failed: failedFeeds,
            };
          }

          const hasUrlFilter =
            this.state.filters.region !== "all" ||
            this.state.filters.type !== "all" ||
            this.state.filters.flavor !== "all" ||
            this.state.filters.fmt !== "all";
          this.applyDerivedState({ refit: hasUrlFilter ? "region" : "default", openPopup: false });
        })
        .catch(() => {
          this.state.stores = [];
          this.state.loadState = {
            stage: "error",
            total: (this.config.sheetUrls || []).length,
            failed: (this.config.sheetUrls || []).length,
          };
          this.renderFilters();
          this.renderAll();
        })
        .finally(() => {
          this.isLoading = false;
        });
    }

    fetchSheet(url) {
      return fetch(url, { credentials: "omit" })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.text();
        })
        .then((text) => this.parseSheet(text));
    }

    parseSheet(text) {
      const rows = this.parseCsv(text || "");
      if (rows.length < 2) return [];
      const headers = rows[0].map((header) => this.normalizeHeader(header));
      const stores = [];

      for (let index = 1; index < rows.length; index += 1) {
        const row = rows[index];
        const hasContent = row.some((cell) => this.normalizeCell(cell) !== "");
        if (!hasContent) continue;
        const store = this.rowToStore(headers, row);
        if (store) stores.push(store);
      }

      return stores;
    }

    parseCsv(rawText) {
      const text = (rawText || "").replace(/^\uFEFF/, "");
      const rows = [];
      let row = [];
      let cell = "";
      let inQuotes = false;

      for (let index = 0; index < text.length; index += 1) {
        const char = text[index];
        const next = text[index + 1];

        if (inQuotes) {
          if (char === '"' && next === '"') {
            cell += '"';
            index += 1;
          } else if (char === '"') {
            inQuotes = false;
          } else {
            cell += char;
          }
          continue;
        }

        if (char === '"') {
          inQuotes = true;
          continue;
        }

        if (char === ",") {
          row.push(cell);
          cell = "";
          continue;
        }

        if (char === "\r") {
          if (next === "\n") index += 1;
          row.push(cell);
          rows.push(row);
          row = [];
          cell = "";
          continue;
        }

        if (char === "\n") {
          row.push(cell);
          rows.push(row);
          row = [];
          cell = "";
          continue;
        }

        cell += char;
      }

      if (cell !== "" || row.length) {
        row.push(cell);
        rows.push(row);
      }

      return rows;
    }

    normalizeHeader(header) {
      const normalized = this.normalizeCell(header)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

      if (/^(id|store_id|location_id)$/.test(normalized)) return "id";
      if (/^(store_name|account_name|name|location)$/.test(normalized)) return "store_name";
      if (/^(addr|address|address_1|street)$/.test(normalized)) return "address";
      if (/^(city|town|municipality)$/.test(normalized)) return "city";
      if (/^(state|province|territory_state)$/.test(normalized)) return "state";
      if (/^(zip|zipcode|zip_code|postal|postal_code)$/.test(normalized)) return "zip";
      if (/^(lat|latitude)$/.test(normalized)) return "latitude";
      if (/^(lng|lon|long|longitude)$/.test(normalized)) return "longitude";
      if (/^(store_type|type|channel|category)$/.test(normalized)) return "store_type";
      if (/^(region|area|territory|market)$/.test(normalized)) return "region";
      if (/^(products|product|brands|items|skus)$/.test(normalized)) return "products";
      if (/^(formats|format|packaging|package|serve|format_type)$/.test(normalized)) return "formats";
      if (/^(active|status|enabled)$/.test(normalized)) return "active";
      if (/^(phone|telephone|tel)$/.test(normalized)) return "phone";
      if (/^(hours|open|opening_hours|store_hours)$/.test(normalized)) return "hours";
      if (/^(website|url|site|web)$/.test(normalized)) return "website";
      return normalized;
    }

    rowToStore(headers, values) {
      const row = {};

      headers.forEach((header, index) => {
        row[header] = this.normalizeCell(values[index]);
      });

      if (row.active && /^(false|0|no|inactive|disabled)$/i.test(row.active)) return null;

      const lat = Number.parseFloat(row.latitude);
      const lng = Number.parseFloat(row.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !row.store_name) return null;

      const products = this.parseProducts(row.products, row.formats);
      const name = publicStoreName(row.store_name);
      const type = this.normalizeStoreType(row.store_type);
      const region = this.normalizeRegion(row.region);
      const id = publicStoreId({
        id: row.id,
        storeName: row.store_name,
        address: row.address,
        city: row.city,
        state: row.state,
        zip: row.zip,
        latitude: lat,
        longitude: lng,
      });

      // Every field a customer can read goes through the same boundary. An
      // operator annotation lands in address line 2 at least as often as in the
      // retailer's name, and the address shows in the popup, the result meta
      // line, and the text the search box matches against.
      return {
        id,
        name,
        addr: publicStoreText(row.address),
        city: publicStoreText(row.city),
        state: publicStoreText(row.state),
        zip: publicStoreText(row.zip),
        lat,
        lng,
        type,
        region,
        products,
        phone: publicStoreText(row.phone),
        hours: publicStoreText(row.hours),
        website: publicStoreWebsite(row.website),
      };
    }

    parseProducts(rawProducts, rawFormats) {
      const productNames = this.splitCommaList(rawProducts);
      const formatGroups = this.splitCommaList(rawFormats);

      return productNames.map((rawName, index) => {
        const formats = this.normalizeFormats(formatGroups[index]);
        // The beer list is customer-facing text from the same feed row.
        const name = publicStoreText(rawName);
        return {
          name,
          formats: formats.length ? formats : ["Can"],
        };
      });
    }

    splitCommaList(value) {
      return (value || "")
        .split(",")
        .map((item) => this.normalizeCell(item))
        .filter(Boolean);
    }

    normalizeFormats(value) {
      const formats = (value || "Can")
        .split("/")
        .map((item) => this.normalizeCell(item).toLowerCase())
        .map((item) => {
          if (item.indexOf("draft") !== -1) return "Draft";
          if (item.indexOf("can") !== -1) return "Can";
          return "";
        })
        .filter(Boolean);

      return Array.from(new Set(formats));
    }

    normalizeStoreType(value) {
      const normalized = this.normalizeCell(value).toLowerCase();
      if (normalized.indexOf("grocery") !== -1) return "Grocery";
      if (normalized.indexOf("bottle") !== -1) return "Bottle Shop";
      if (normalized.indexOf("bar") !== -1 || normalized.indexOf("restaurant") !== -1) return "Bar & Restaurant";
      return this.normalizeCell(value);
    }

    normalizeRegion(value) {
      const normalized = this.normalizeCell(value).toLowerCase();
      if (normalized === "california") return "California";
      if (normalized === "wisconsin") return "Wisconsin";
      if (normalized === "illinois") return "Illinois";
      if (normalized === "midwest") return "Midwest";
      if (normalized === "west coast" || normalized === "westcoast") return "West Coast";
      return this.normalizeCell(value);
    }

    normalizeCell(value) {
      return String(value == null ? "" : value)
        .replace(/\s+/g, " ")
        .trim();
    }

    dedupeStores(stores) {
      const seen = new Map();
      stores.forEach((store) => {
        if (!seen.has(store.id)) {
          seen.set(store.id, store);
        }
      });
      return Array.from(seen.values());
    }

    applyDerivedState(options) {
      const settings = options || {};
      this.state.filteredStores = this.getFilteredStores();

      if (this.state.selectedId && !this.state.filteredStores.some((store) => store.id === this.state.selectedId)) {
        this.clearSelection(false);
      }

      if (!this.state.selectedId && settings.autoSelectFirst && this.state.filteredStores.length) {
        this.state.selectedId = this.state.filteredStores[0].id;
      }

      this.syncUrlState();
      this.renderFilters();
      this.renderAll();
      this.syncMarkers();

      if (this.state.selectedId && settings.openPopup) {
        this.openPopup(this.getSelectedStore());
      }

      if (settings.refit === "region") {
        this.fitToRegionOrResults();
      } else if (settings.refit === "results") {
        this.fitResults();
      } else if (settings.refit === "default" && !this.state.selectedId && !this.state.locationAnchor) {
        this.fitDefaultView();
      }
    }

    getFilteredStores() {
      const query = (this.state.queryText || "").trim().toLowerCase();
      const filters = this.state.filters;
      const anchor = this.state.locationAnchor;
      const maxDist = this.state.maxDistance;

      return this.state.stores
        .map((store) => {
          const distanceMiles = anchor ? this.haversine(anchor.lat, anchor.lng, store.lat, store.lng) : null;
          return Object.assign({}, store, { distanceMiles });
        })
        .filter((store) => {
          if (filters.region !== "all" && !this.matchesRegionFilter(store, filters.region)) return false;
          if (filters.type !== "all" && store.type !== filters.type) return false;
          if (filters.flavor !== "all" && !store.products.some((product) => product.name === filters.flavor)) return false;
          if (filters.fmt !== "all") {
            const formatMatch = store.products.some((product) => {
              if (filters.flavor !== "all" && product.name !== filters.flavor) return false;
              return product.formats.indexOf(filters.fmt) !== -1;
            });
            if (!formatMatch) return false;
          }
          if (maxDist && store.distanceMiles != null && store.distanceMiles > maxDist) return false;
          if (!query) return true;
          const searchBlob = [
            store.name,
            store.addr,
            store.city,
            store.state,
            store.zip,
            store.region,
            store.type,
            store.products.map((product) => product.name).join(" "),
          ]
            .join(" ")
            .toLowerCase();
          return searchBlob.indexOf(query) !== -1;
        })
        .sort((left, right) => {
          if (anchor && left.distanceMiles != null && right.distanceMiles != null) {
            if (left.distanceMiles !== right.distanceMiles) return left.distanceMiles - right.distanceMiles;
          }
          return left.name.localeCompare(right.name);
        });
    }

    setFilter(key, value) {
      this.state.filters[key] = value;
      this.state.notice = null;
      this.applyDerivedState({ refit: key === "region" ? "region" : null, openPopup: false });
    }

    useMyLocation() {
      this.hideLocationPrompt();
      if (!navigator.geolocation) {
        this.state.notice = { tone: "warning", message: "This browser does not support geolocation." };
        this.renderAll();
        return;
      }

      this.state.notice = { tone: "loading", message: "Fetching your location..." };
      this.renderAll();

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.setLocationAnchor({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            label: "your location",
            source: "device",
          });
          this.state.notice = null;
          this.state.maxDistance = 10;
          this.updateDistanceChips();
          this.applyDerivedState({ refit: "results", openPopup: false });
          // Keep sheet collapsed — map first, always
          this.collapseMobileSheet();
        },
        () => {
          this.state.notice = { tone: "warning", message: "Location access was blocked. Results remain unchanged." };
          this.renderAll();
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    applyLocationQuery() {
      if (!this.dom.locationInput) return;
      const value = this.normalizeCell(this.dom.locationInput.value);
      if (!value) {
        this.state.notice = { tone: "warning", message: "Enter a city or ZIP code to set a location anchor." };
        this.renderAll();
        return;
      }

      const token = this.locationRequestToken + 1;
      this.locationRequestToken = token;
      this.state.notice = { tone: "loading", message: `Searching for ${value}...` };
      this.renderAll();

      fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&countrycodes=us&limit=1&q=${encodeURIComponent(value)}`
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.json();
        })
        .then((payload) => {
          if (token !== this.locationRequestToken) return;
          if (!payload || !payload.length) {
            this.state.notice = { tone: "warning", message: `No location match was found for ${value}.` };
            this.renderAll();
            return;
          }

          const match = payload[0];
          this.setLocationAnchor({
            lat: Number.parseFloat(match.lat),
            lng: Number.parseFloat(match.lon),
            label: this.compactPlaceLabel(match.display_name || value),
            source: "search",
          });
          this.state.notice = {
            tone: "ready",
            message: `Distance sorting is now anchored to ${this.compactPlaceLabel(match.display_name || value)}.`,
          };
          this.applyDerivedState({ openPopup: false });
          this.fitResults();
          this.autoSelectTopResult();
        })
        .catch(() => {
          if (token !== this.locationRequestToken) return;
          this.state.notice = { tone: "warning", message: "Location lookup failed. Results remain unchanged." };
          this.renderAll();
        });
    }

    compactPlaceLabel(value) {
      return String(value || "")
        .split(",")
        .slice(0, 2)
        .map((item) => this.normalizeCell(item))
        .filter(Boolean)
        .join(", ");
    }

    setLocationAnchor(anchor) {
      this.state.locationAnchor = anchor;
      if (!this.map || !window.L) return;

      const latLng = [anchor.lat, anchor.lng];
      if (!this.userMarker) {
        this.userMarker = window.L.circleMarker(latLng, {
          radius: 9,
          fillColor: "#f4eee8",
          fillOpacity: 1,
          color: "#d63d48",
          weight: 3,
        }).addTo(this.map);
      } else {
        this.userMarker.setLatLng(latLng).addTo(this.map);
      }
    }

    clearAll() {
      this.dom.queryInput.value = "";
      this.state.queryText = "";
      this.state.filters = { region: "all", type: "all", flavor: "all", fmt: "all" };
      this.state.notice = { tone: "ready", message: "All filters, search terms, and location sorting have been cleared." };
      this.locationRequestToken += 1;
      this.state.locationAnchor = null;
      this.state.maxDistance = null;
      if (this.userMarker && this.map) {
        this.map.removeLayer(this.userMarker);
      }
      this.userMarker = null;
      this.clearSelection(false);
      this.applyDerivedState({ refit: "default", openPopup: false });
      this.fitDefaultView();
    }

    clearSelection(shouldRender) {
      this.state.selectedId = null;
      if (this.activePopup && this.map) {
        this.map.closePopup(this.activePopup);
      }
      this.activePopup = null;
      if (shouldRender !== false) {
        this.renderAll();
        this.syncMarkers();
      }
    }

    autoSelectTopResult() {
      if (!this.state.filteredStores.length) return;
      this.selectStore(this.state.filteredStores[0].id, { pan: false, openPopup: true, scrollIntoView: false });
    }

    selectStore(id, options) {
      const settings = Object.assign({ pan: true, openPopup: true, scrollIntoView: true }, options || {});
      const store = this.state.filteredStores.find((entry) => entry.id === id) || this.state.stores.find((entry) => entry.id === id);
      if (!store) return;

      this.state.selectedId = id;
      this.renderAll();
      this.syncMarkers();

      const isMobile = window.innerWidth <= 960;
      if (settings.pan && this.map) {
        if (isMobile) {
          // On mobile, position the pin in the lower 60% of the map so the popup has room above
          const mapSize = this.map.getSize();
          const targetPoint = this.map.project([store.lat, store.lng], this.map.getZoom());
          // Shift the center up so the pin lands ~60% down from the top
          const offsetY = mapSize.y * 0.15;
          const adjustedCenter = this.map.unproject(
            [targetPoint.x, targetPoint.y - offsetY],
            this.map.getZoom()
          );
          this.map.flyTo(adjustedCenter, Math.max(this.map.getZoom(), 11), { duration: 0.5 });
        } else {
          this.map.flyTo([store.lat, store.lng], Math.max(this.map.getZoom(), 11), { duration: 0.75 });
        }
      }
      if (settings.openPopup) {
        // On mobile, delay popup slightly so the pan finishes first
        if (isMobile && settings.pan) {
          setTimeout(() => this.openPopup(store), 550);
        } else {
          this.openPopup(store);
        }
      }
      if (settings.scrollIntoView) {
        this.scrollSelectionIntoView(id);
      }
    }

    scrollSelectionIntoView(id) {
      this.root.querySelectorAll(`.store-locator-result[data-store-id="${id}"]`).forEach((target) => {
        target.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    }

    openPopup(store) {
      if (!this.map || !this.markers.has(store.id)) return;
      const marker = this.markers.get(store.id);
      const directionsUrl = this.getDirectionsUrl(store);
      const websiteUrl = this.normalizeWebsite(store.website);
      const phoneLink = this.normalizePhoneLink(store.phone);
      const actions = [
        `<a class="store-locator-action-link store-locator-action-link--primary" href="${this.escapeAttribute(
          directionsUrl
        )}" target="_blank" rel="noopener noreferrer" data-locator-track="directions_click" data-store-name="${this.escapeAttribute(store.name)}">Directions</a>`,
      ];

      if (phoneLink) {
        actions.push(
          `<a class="store-locator-action-link" href="${this.escapeAttribute(phoneLink)}" data-locator-track="call_click" data-store-name="${this.escapeAttribute(store.name)}">Call</a>`
        );
      }

      if (websiteUrl) {
        actions.push(
          `<a class="store-locator-action-link" href="${this.escapeAttribute(websiteUrl)}" target="_blank" rel="noopener noreferrer">Website</a>`
        );
      }

      const beerList = store.products.map((p) => {
        const fmts = p.formats.join("/");
        return `<span class="store-locator-popup__beer-line">${this.getBeerEmoji(p.name)} ${this.escapeHtml(p.name)} <span class="store-locator-popup__beer-fmt">\u00b7 ${this.escapeHtml(fmts)}</span></span>`;
      }).join("");

      const isMobile = window.innerWidth <= 960;
      const popup = window.L.popup({
        maxWidth: isMobile ? 280 : 320,
        closeButton: true,
        autoPan: true,
        autoPanPaddingTopLeft: [12, isMobile ? 80 : 12],
        autoPanPaddingBottomRight: [12, isMobile ? 70 : 12],
        offset: [0, -8]
      })
        .setLatLng([store.lat, store.lng])
        .setContent(
          `<div class="store-locator-popup">` +
            `<h3 class="store-locator-popup__title">${this.escapeHtml(store.name)}</h3>` +
            `<p class="store-locator-popup__meta">${this.renderTypePill(store.type)}</p>` +
            `<p class="store-locator-popup__address">${this.escapeHtml(this.formatAddress(store, true))}</p>` +
            (beerList ? `<div class="store-locator-popup__beers">${beerList}</div>` : "") +
            `<div class="store-locator-popup__actions">${actions.join("")}</div>` +
            `<p class="store-locator-popup__callout">Call ahead to check availability</p>` +
          `</div>`
        );

      this.activePopup = popup;
      marker.bindPopup(popup).openPopup();
      this.collapseMobileSheet();
      // Expand sheet when popup closes
      popup.on("remove", () => this.expandMobileSheet());
    }

    renderAll() {
      this.renderStatus();
      this.renderCounts();
      this.renderSelected();
      this.renderResults();
      this.renderMapCaption();
      this.updateActionAvailability();
      this.renderMobileSheetTitle();
      this.renderFilterBadge();
    }

    renderMobileSheetTitle() {
      const title = this.root.querySelector(".store-locator-results-head--mobile .store-locator-results-head__title");
      if (!title) return;
      const count = this.state.filteredStores.length;
      const sheet = this.root.querySelector(".store-locator-sheet");
      const isCollapsed = sheet && sheet.classList.contains("is-collapsed");
      const chevron = isCollapsed ? "\u25b2 " : "";
      if (this.state.loadState.stage === "loading") {
        title.textContent = chevron + "Finding stores\u2026";
      } else if (count === 0) {
        title.textContent = "No stores found";
      } else {
        title.textContent = chevron + count + (count === 1 ? " store nearby" : " stores nearby");
      }
    }

    renderFilterBadge() {
      const f = this.state.filters;
      const active = [f.region, f.type, f.flavor, f.fmt].filter((v) => v !== "all").length;
      // Update badge on both the text toggle and icon button
      this.root.querySelectorAll(".store-locator-filter-toggle, .store-locator-filter-icon").forEach((btn) => {
        let badge = btn.querySelector(".store-locator-filter-badge");
        if (active > 0) {
          if (!badge) {
            badge = document.createElement("span");
            badge.className = "store-locator-filter-badge";
            btn.appendChild(badge);
          }
          badge.textContent = active;
        } else if (badge) {
          badge.remove();
        }
      });
    }

    renderStatus() {
      const loadState = this.state.loadState;
      if (loadState.stage === "idle" || loadState.stage === "loading") {
        this.dom.status.textContent = "Finding stores...";
        this.dom.status.className = "store-locator-status is-loading";
      } else if (loadState.stage === "error") {
        this.dom.status.textContent = "Temporarily unavailable.";
        this.dom.status.className = "store-locator-status is-error";
      } else {
        this.dom.status.textContent = "";
        this.dom.status.className = "store-locator-status";
      }
      if (this.dom.summary) this.dom.summary.textContent = "";
    }

    renderCounts() {
      // Counts hidden by design — no visible count display
    }

    renderSelected() {
      const selected = this.getSelectedStore();
      const markup = selected ? this.renderSelectedCard(selected) : "";
      this.dom.selected.forEach((container) => {
        container.innerHTML = markup; // Safe: all values passed through escapeHtml
      });
    }

    getSelectedStore() {
      if (!this.state.selectedId) return null;
      return this.state.filteredStores.find((store) => store.id === this.state.selectedId) || null;
    }

    renderSelectedCard(store) {
      const directionUrl = this.getDirectionsUrl(store);
      const websiteUrl = this.normalizeWebsite(store.website);
      const phoneLink = this.normalizePhoneLink(store.phone);
      const actionLinks = [
        `<a class="store-locator-action-link store-locator-action-link--primary" href="${this.escapeAttribute(
          directionUrl
        )}" target="_blank" rel="noopener noreferrer" data-locator-track="directions_click" data-store-name="${this.escapeAttribute(store.name)}">Get directions</a>`,
      ];

      if (phoneLink) {
        actionLinks.push(`<a class="store-locator-action-link" href="${this.escapeAttribute(phoneLink)}" data-locator-track="call_click" data-store-name="${this.escapeAttribute(store.name)}">Call store</a>`);
      }

      const websiteMarkup = websiteUrl
        ? `<p class="store-locator-selection-card__website"><a href="${this.escapeAttribute(websiteUrl)}" target="_blank" rel="noopener noreferrer">Website</a></p>`
        : "";

      return (
        `<div class="store-locator-selection-card">` +
        `<div class="store-locator-selection-card__header">` +
        `<div>` +
        `<p class="store-locator-selection-card__eyebrow">Selected store</p>` +
        `<h2 class="store-locator-selection-card__title">${this.escapeHtml(store.name)}</h2>` +
        `</div>` +
        `<button type="button" class="store-locator-selection-card__close" data-locator-action="clear-selection" aria-label="Clear selection">` +
        `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M6 6 18 18M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>` +
        `</button>` +
        `</div>` +
        `<div class="store-locator-selection-card__meta">` +
        this.renderTypePill(store.type) +
        this.renderDistancePill(store.distanceMiles) +
        (store.region ? `<span class="store-locator-pill">${this.escapeHtml(store.region)}</span>` : "") +
        `</div>` +
        `<p class="store-locator-selection-card__address">${this.escapeHtml(this.formatAddress(store, true))}</p>` +
        websiteMarkup +
        `<div class="store-locator-selection-card__products">${this.renderProductBlocks(store.products)}</div>` +
        `<div class="store-locator-selection-card__actions">${actionLinks.join("")}</div>` +
        `<p class="store-locator-selection-card__callout">Call ahead to check availability</p>` +
        `</div>`
      );
    }

    renderEmptySelection() {
      const title =
        this.state.loadState.stage === "loading"
          ? "Preparing the locator"
          : this.state.loadState.stage === "error"
            ? "Live feed unavailable"
            : "Choose a store";
      const body =
        this.state.loadState.stage === "loading"
          ? "Store data is loading. Filters, results, and markers will populate as soon as the live sheets respond."
          : this.state.loadState.stage === "error"
            ? "The locator feed is currently unavailable. Retry the feed to restore results."
            : "Select a result card or map marker to inspect beers, distance, and directions for that location.";
      const actions =
        this.state.loadState.stage === "error"
          ? `<div class="store-locator-state__actions"><button type="button" class="store-locator-button store-locator-button--accent" data-locator-action="retry">Retry feed</button></div>`
          : "";

      return (
        `<div class="store-locator-selection-card store-locator-selection-card--empty">` +
        `<p class="store-locator-selection-card__eyebrow">Selection</p>` +
        `<h2 class="store-locator-selection-card__title">${this.escapeHtml(title)}</h2>` +
        `<p class="store-locator-selection-card__note">${this.escapeHtml(body)}</p>` +
        actions +
        `</div>`
      );
    }

    renderResults() {
      this.dom.results.forEach((container) => {
        const isMobileContainer = container.classList.contains("store-locator-results--mobile");
        // Safe: all values passed through escapeHtml/escapeAttribute
        container.innerHTML = isMobileContainer ? this.buildMobileResultsMarkup() : this.buildResultsMarkup();
      });
    }

    buildMobileResultsMarkup() {
      if (this.state.loadState.stage === "loading") {
        return this.renderStateCard("Finding stores", "Looking for Dokkaebier near you...");
      }
      if (this.state.loadState.stage === "error") {
        return this.renderStateCard("Temporarily unavailable", "Store information is temporarily unavailable. Please try again.", "retry", "Try again");
      }
      if (!this.state.filteredStores.length) {
        return this.renderStateCard("No stores found", "No stores match your search. Try a different location or adjust your filters.", "clear-all", "Reset");
      }
      return this.state.filteredStores.map((store) => this.renderMobileResultCard(store)).join("");
    }

    renderMobileResultCard(store) {
      const selectedClass = store.id === this.state.selectedId ? " is-selected" : "";
      const beerList = store.products.map((p) =>
        `<span class="store-locator-beer-tag">${this.getBeerEmoji(p.name)} ${this.escapeHtml(p.name)}</span>`
      ).join("");
      const dirUrl = this.getDirectionsUrl(store);
      // Safe: all dynamic values passed through escapeHtml/escapeAttribute
      return (
        `<div class="store-locator-result-mobile${selectedClass}" data-store-id="${this.escapeAttribute(store.id)}">` +
        `<div class="store-locator-result-mobile__body">` +
        `<h3 class="store-locator-result-mobile__title">${this.escapeHtml(store.name)}</h3>` +
        `<div class="store-locator-result-mobile__meta">` +
        this.renderTypePill(store.type) +
        this.renderDistancePill(store.distanceMiles) +
        `</div>` +
        `<div class="store-locator-result-mobile__beers">${beerList}</div>` +
        `</div>` +
        `<a class="store-locator-result-mobile__directions" href="${this.escapeAttribute(dirUrl)}" target="_blank" rel="noopener noreferrer" aria-label="Get directions" data-locator-track="directions_click" data-store-name="${this.escapeAttribute(store.name)}">` +
        `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" width="18" height="18"><path d="M7 17L17 7M17 7H7M17 7v10"/></svg>` +
        `</a>` +
        `</div>`
      );
    }

    buildResultsMarkup() {
      if (this.state.loadState.stage === "loading") {
        return this.renderStateCard("Finding stores", "Looking for Dokkaebier near you...");
      }

      if (this.state.loadState.stage === "error") {
        return this.renderStateCard(
          "Temporarily unavailable",
          "Store information is temporarily unavailable. Please try again.",
          "retry",
          "Try again"
        );
      }

      if (!this.state.filteredStores.length) {
        return this.renderStateCard(
          "No stores found",
          "No stores match your search. Try a different location or adjust your filters.",
          "clear-all",
          "Reset"
        );
      }

      return this.state.filteredStores.map((store) => this.renderResultCard(store)).join("");
    }

    renderStateCard(title, body, action, actionLabel) {
      const actions =
        action && actionLabel
          ? `<div class="store-locator-state__actions"><button type="button" class="store-locator-button store-locator-button--accent" data-locator-action="${this.escapeAttribute(
              action
            )}">${this.escapeHtml(actionLabel)}</button></div>`
          : "";

      return (
        `<div class="store-locator-state">` +
        `<p class="store-locator-state__eyebrow">Store locator</p>` +
        `<h3 class="store-locator-state__title">${this.escapeHtml(title)}</h3>` +
        `<p class="store-locator-state__body">${this.escapeHtml(body)}</p>` +
        actions +
        `</div>`
      );
    }

    renderResultCard(store) {
      const selectedClass = store.id === this.state.selectedId ? " is-selected" : "";
      const beerList = store.products.map((p) =>
        `<span class="store-locator-beer-tag">${this.getBeerEmoji(p.name)} ${this.escapeHtml(p.name)}</span>`
      ).join("");
      return (
        `<button type="button" class="store-locator-result${selectedClass}" data-store-id="${this.escapeAttribute(
          store.id
        )}" aria-pressed="${store.id === this.state.selectedId ? "true" : "false"}">` +
        `<div class="store-locator-result__header">` +
        `<div>` +
        `<h3 class="store-locator-result__title">${this.escapeHtml(store.name)}</h3>` +
        `<p class="store-locator-result__address">${this.escapeHtml(this.formatAddress(store, false))}</p>` +
        `</div>` +
        `<div class="store-locator-result__meta">` +
        this.renderTypePill(store.type) +
        this.renderDistancePill(store.distanceMiles) +
        `</div>` +
        `</div>` +
        `<div class="store-locator-result__beers">${beerList}</div>` +
        `</button>`
      );
    }

    getBeerEmoji(name) {
      const map = { "Yuza Blonde": "\ud83c\udf4b", "K-Beer": "\u262f\ufe0f", "Kimchi Sour": "\ud83c\udf36\ufe0f", "Lychee Blonde": "\ud83c\udf38", "Dokkaebi IPA": "\ud83c\udf7a", "Milk Stout": "\ud83e\udd5b" };
      return map[name] || "\ud83c\udf7a";
    }

    visibleProductsForStore(store) {
      if (this.state.filters.flavor === "all") return store.products;
      return store.products.filter((product) => product.name === this.state.filters.flavor);
    }

    buildFootnote(store) {
      const parts = [];
      if (store.city && store.state) parts.push(`${store.city}, ${store.state}`);
      if (store.hours) parts.push(store.hours);
      return parts.join(" / ");
    }

    renderProductBlocks(products) {
      return products
        .map((product) => {
          const meta = this.getProductMeta(product.name);
          const formats = product.formats
            .map(
              (format) =>
                `<span class="store-locator-product__format" data-format="${this.escapeAttribute(format)}">${this.escapeHtml(format)}</span>`
            )
            .join("");
          return (
            `<span class="store-locator-product" style="--product-tone:${this.escapeAttribute(meta.tone)}">` +
            `<span class="store-locator-product__icon">${this.escapeHtml(meta.icon)}</span>` +
            `<span class="store-locator-product__stack">` +
            `<span class="store-locator-product__name">${this.escapeHtml(meta.label)}</span>` +
            `<span class="store-locator-product__formats">${formats}</span>` +
            `</span>` +
            `</span>`
          );
        })
        .join("");
    }

    renderDetailCell(label, value) {
      return (
        `<div class="store-locator-detail">` +
        `<span class="store-locator-detail__label">${this.escapeHtml(label)}</span>` +
        `<span class="store-locator-detail__value">${this.escapeHtml(value)}</span>` +
        `</div>`
      );
    }

    renderTypePill(type) {
      const meta = TYPE_META[type] || { className: "", label: type || "Store" };
      return `<span class="store-locator-pill ${meta.className || ""}">${this.escapeHtml(meta.label)}</span>`;
    }

    renderDistancePill(distanceMiles) {
      if (distanceMiles == null) return "";
      return `<span class="store-locator-pill store-locator-pill--distance">${distanceMiles.toFixed(1)} mi</span>`;
    }

    renderMapCaption() {
      if (!this.dom.mapCaption) return;
      // Keep caption minimal — only show during loading/error states
      if (this.state.loadState.stage === "loading") {
        this.dom.mapCaption.textContent = "";
        return;
      }
      if (this.state.loadState.stage === "error") {
        this.dom.mapCaption.textContent = "Store feed unavailable.";
        return;
      }
      this.dom.mapCaption.textContent = "";
    }

    updateActionAvailability() {
      const hasResults = this.state.filteredStores.length > 0;
      this.dom.fitButtons.forEach((button) => {
        button.disabled = !hasResults;
      });
      this.dom.retryButtons.forEach((button) => {
        button.hidden = !(this.state.loadState.stage === "error" || this.state.loadState.stage === "partial");
      });
    }

    syncMarkers() {
      if (!this.map || !this.markerLayer) return;

      const visibleIds = new Set(this.state.filteredStores.map((store) => store.id));

      this.state.stores.forEach((store) => {
        const marker = this.ensureMarker(store);
        const isVisible = visibleIds.has(store.id);
        const isSelected = store.id === this.state.selectedId;

        marker.setIcon(this.makeMarkerIcon(store.type, isSelected));
        if (isVisible) {
          if (!this.markerLayer.hasLayer(marker)) {
            marker.addTo(this.markerLayer);
          }
        } else if (this.markerLayer.hasLayer(marker)) {
          this.markerLayer.removeLayer(marker);
        }
      });

      if (this.state.selectedId && !visibleIds.has(this.state.selectedId) && this.activePopup && this.map) {
        this.map.closePopup(this.activePopup);
        this.activePopup = null;
      }
    }

    ensureMarker(store) {
      if (this.markers.has(store.id)) {
        return this.markers.get(store.id);
      }

      const marker = window.L.marker([store.lat, store.lng], { icon: this.makeMarkerIcon(store.type, false) });
      marker.on("click", () => {
        this.selectStore(store.id, { pan: false, openPopup: true, scrollIntoView: true });
      });
      this.markers.set(store.id, marker);
      return marker;
    }

    clearMapMarkers() {
      if (!this.markerLayer) return;
      this.markerLayer.clearLayers();
    }

    makeMarkerIcon(type, isSelected) {
      const meta = TYPE_META[type] || TYPE_META["Bar & Restaurant"];
      return window.L.divIcon({
        className: "",
        iconSize: isSelected ? [24, 24] : [18, 18],
        iconAnchor: isSelected ? [12, 12] : [9, 9],
        popupAnchor: [0, -12],
        html: `<span class="store-locator-marker${isSelected ? " is-selected" : ""}" style="--marker-tone:${this.escapeAttribute(
          meta.tone
        )}"></span>`,
      });
    }

    fitDefaultView() {
      if (!this.map) return;
      const center = Array.isArray(this.config.defaultCenter) ? this.config.defaultCenter : [39.5, -98.35];
      const zoom = Number.isFinite(this.config.defaultZoom) ? this.config.defaultZoom : 4;
      this.map.flyTo(center, zoom, { duration: 0.75 });
    }

    fitToRegionOrResults() {
      const region = this.state.filters.region;
      if (region && region !== "all" && this.state.filteredStores.length) {
        const bounds = window.L.latLngBounds(this.state.filteredStores.map(function(s) { return [s.lat, s.lng]; }));
        const isMobile = window.innerWidth <= 960;
        // Mobile: pad top for search bar, bottom for collapsed sheet. Desktop: pad left for sidebar.
        const padding = isMobile
          ? { paddingTopLeft: [20, 70], paddingBottomRight: [20, 60] }
          : { paddingTopLeft: [40, 40], paddingBottomRight: [40, 40] };
        this.map.flyToBounds(bounds, { ...padding, maxZoom: isMobile ? 11 : 10, duration: 0.75 });
        return;
      }
      this.fitResults();
    }

    fitResults() {
      if (!this.map) return;
      if (!this.state.filteredStores.length) {
        this.fitDefaultView();
        return;
      }

      const isMobile = window.innerWidth <= 960;
      // Mobile: pad top for search bar (~70px), bottom for collapsed sheet (~60px)
      // Desktop: pad left for sidebar
      const mobilePad = { paddingTopLeft: [20, 70], paddingBottomRight: [20, 60] };
      const desktopPad = { paddingTopLeft: [42, 42], paddingBottomRight: [42, 42] };
      const pad = isMobile ? mobilePad : desktopPad;

      if (this.state.locationAnchor) {
        const anchor = this.state.locationAnchor;
        const focusStores = this.state.filteredStores.slice(0, Math.min(8, this.state.filteredStores.length));
        const points = focusStores.map((store) => [store.lat, store.lng]);
        points.push([anchor.lat, anchor.lng]);

        if (focusStores.length <= 1) {
          const target = focusStores[0];
          if (target) {
            this.map.flyTo([target.lat, target.lng], 11, { duration: 0.75 });
          } else {
            this.map.flyTo([anchor.lat, anchor.lng], 11, { duration: 0.75 });
          }
          return;
        }

        const anchorBounds = window.L.latLngBounds(points);
        this.map.flyToBounds(anchorBounds, { ...pad, maxZoom: isMobile ? 11 : 12, duration: 0.75 });
        return;
      }

      if (this.state.filteredStores.length === 1) {
        const store = this.state.filteredStores[0];
        this.map.flyTo([store.lat, store.lng], 11, { duration: 0.75 });
        return;
      }

      const bounds = window.L.latLngBounds(this.state.filteredStores.map((store) => [store.lat, store.lng]));
      this.map.flyToBounds(bounds, { ...pad, maxZoom: isMobile ? 11 : 12, duration: 0.75 });
    }

    getDirectionsUrl(store) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(this.formatAddress(store, true))}`;
    }

    formatAddress(store, includeZip) {
      const parts = [store.addr];
      const locality = [store.city, store.state].filter(Boolean).join(", ");
      if (locality) parts.push(locality);
      if (includeZip && store.zip) parts.push(store.zip);
      return parts.filter(Boolean).join(", ");
    }

    normalizeWebsite(rawUrl) {
      const value = this.normalizeCell(rawUrl);
      if (!value) return "";
      const withProtocol = /^(https?:)?\/\//i.test(value) ? value : `https://${value}`;
      try {
        const url = new URL(withProtocol);
        return url.href;
      } catch (error) {
        return "";
      }
    }

    normalizePhoneLink(rawPhone) {
      const cleaned = String(rawPhone || "").replace(/[^0-9+]/g, "");
      if (!cleaned) return "";
      return `tel:${cleaned}`;
    }

    haversine(lat1, lng1, lat2, lng2) {
      const toRadians = (degrees) => (degrees * Math.PI) / 180;
      const earthRadiusMiles = 3958.8;
      const deltaLat = toRadians(lat2 - lat1);
      const deltaLng = toRadians(lng2 - lng1);
      const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(toRadians(lat1)) *
          Math.cos(toRadians(lat2)) *
          Math.sin(deltaLng / 2) *
          Math.sin(deltaLng / 2);

      return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    getProductMeta(name) {
      const configured = this.productMeta[name];
      if (configured) return configured;
      const icon = this.normalizeCell(name).slice(0, 2).toUpperCase();
      return {
        label: name,
        icon: icon || "DK",
        order: 999,
        tone: "#b8954f",
      };
    }

    escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }

    trackEvent(eventName, storeName) {
      if (typeof window.Shopify !== "undefined" && typeof window.Shopify.analytics !== "undefined") {
        try { window.Shopify.analytics.publish(eventName, { store_name: storeName }); } catch (e) { /* noop */ }
      }
      if (typeof window.gtag === "function") {
        window.gtag("event", eventName, { store_name: storeName });
      }
    }
  }

  function parseConfig(root) {
    const configId = root.getAttribute("data-config-id");
    if (!configId) return {};
    const node = document.getElementById(configId);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || "{}");
    } catch (error) {
      return {};
    }
  }

  function initStoreLocator() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;
    const locator = new StoreLocator(root, parseConfig(root));
    locator.init();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initStoreLocator);
  } else {
    initStoreLocator();
  }
})();
